import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
// Import Shared Utils
import { formatCurrencyCode } from 'shared-components/src/utils/formatCurrency';
// Import Local Utils
import { deliveryRange } from '../../utils/businessDays';
// Import Hooks
import {
  REPLACEMENT_PHASES,
  PASSWORD_ERRORS,
  DEFAULT_DELIVERY_WINDOW,
} from '../../hooks/useCardReplacement';
// Import Component Styles
import {
  Scrim,
  Dialog,
  StepBody,
  CloseButton,
  Title,
  Steps,
  FieldLabel,
  OptionList,
  Option,
  Indicator,
  Banner,
  BannerLead,
  Address,
  Summary,
  DisclosureAlert,
  Disclosures,
  PasswordField,
  PasswordInput,
  ErrorText,
  Hint,
  Actions,
  FlowButton,
  Centered,
  Spinner,
  SuccessMark,
} from './styles';
import supportContactValues from './SupportContact';
import useDialogFocus from './useDialogFocus';
import { dateFormat } from './CardReplacementBanner';

const bold = chunks => <b>{chunks}</b>;
// A bold lead-in on its own line, as the mockup's banners put it.
const lead = chunks => <BannerLead>{chunks}</BannerLead>;

export const REASONS = ['lost', 'stolen', 'damaged'];
// Lost and stolen cards may already have been used; point to the dispute line.
const DISPUTE_REASONS = ['lost', 'stolen'];
const STEP_KEYS = ['reason', 'delivery', 'confirm'];

const PASSWORD_ERROR_KEYS = {
  [PASSWORD_ERRORS.WRONG]: 'replace-card-wrong-password',
  [PASSWORD_ERRORS.STEP_UP]: 'replace-card-step-up',
};

// Notices that replace the steps entirely; the cardholder can only close the dialog.
const NOTICES = {
  [REPLACEMENT_PHASES.INELIGIBLE]: { id: 'replace-card-ineligible', tone: 'bad' },
  [REPLACEMENT_PHASES.UNAVAILABLE]: { id: 'replace-card-unavailable', tone: 'warn' },
  [REPLACEMENT_PHASES.QUOTE_FAILED]: { id: 'replace-card-quote-failed', tone: 'bad' },
  [REPLACEMENT_PHASES.ALREADY_ORDERED]: { id: 'replace-card-already-ordered', tone: 'info' },
};

const formatAddress = address =>
  [
    address.address1,
    address.address2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(' '),
  ].filter(Boolean);

export const CheckMark = ({ size, pathLength }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M5 12l5 5 9-10" pathLength={pathLength} />
  </svg>
);

CheckMark.propTypes = {
  size: PropTypes.number.isRequired,
  // Set where the tick draws in, so the stroke animation knows its length.
  pathLength: PropTypes.number,
};

CheckMark.defaultProps = {
  pathLength: undefined,
};

const Message = ({ id, values }) => (
  <FormattedMessage id={id} values={{ b: bold, lead, ...supportContactValues(), ...values }} />
);

Message.propTypes = {
  id: PropTypes.string.isRequired,
  values: PropTypes.object, //eslint-disable-line
};

Message.defaultProps = {
  values: {},
};

// A radio or checkbox row with its own drawn control, so the selection can animate.
const ChoiceRow = ({ type, name, value, checked, onChange, align, children }) => (
  <Option $selected={checked} $align={align}>
    <input type={type} name={name} value={value} checked={checked} onChange={onChange} />
    <Indicator $shape={type} data-shape={type} aria-hidden="true">
      {type === 'checkbox' && <CheckMark size={12} pathLength={20} />}
    </Indicator>
    <span>{children}</span>
  </Option>
);

ChoiceRow.propTypes = {
  type: PropTypes.oneOf(['radio', 'checkbox']).isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  align: PropTypes.string,
  children: PropTypes.node.isRequired,
};

ChoiceRow.defaultProps = {
  name: undefined,
  value: undefined,
  align: undefined,
};

// The three-step replacement dialog (BP-246): reason, delivery, confirm with the password.
// The hook owns the quote and the order; the dialog owns what the cardholder has entered.
// The quote only answers eligibility and the fee: the balance and mailing address are the
// ones the dashboard already loaded, passed in by the page.
const ReplaceCardModal = ({
  isOpen,
  phase,
  quote,
  balanceCents,
  mailingAddress,
  passwordError,
  onPasswordChange,
  onSubmit,
  onRetry,
  onClose,
}) => {
  const intl = useIntl();
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [password, setPassword] = useState('');

  // Each opening starts over.
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setReason(null);
      setAcknowledged(false);
      setPassword('');
    }
  }, [isOpen]);

  // A refused password is cleared so it can be typed again.
  useEffect(() => {
    if (passwordError) setPassword('');
  }, [passwordError]);
  // The refused password is cleared; typing a new one dismisses the error (onPasswordChange).
  const passwordInvalid = Boolean(passwordError);

  // No closing with Escape while the order is being placed, as there is no close button then.
  const dialogRef = useDialogFocus({
    isOpen,
    onClose,
    canClose: phase !== REPLACEMENT_PHASES.SUBMITTING,
  });

  if (!isOpen) return null;

  // Amounts arrive in cents and are shown in the quote's currency for the cardholder's
  // locale: $142.30 in English, 142,30 $ in French.
  const money = cents =>
    intl.formatNumber(cents / 100, {
      style: 'currency',
      currency: formatCurrencyCode(quote && quote.currency) || 'CAD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  // The fee as a deduction from the balance: -$5.00, or $0.00 when there is none.
  const feeDeduction = cents => (cents ? money(-cents) : money(0));

  const actionButton = (key, onClick, tone = 'secondary', props = {}) => (
    <FlowButton
      buttonText={intl.messages[`replace-card-${key}`]}
      page="replace-card"
      buttonFor={key}
      $tone={tone}
      onClick={onClick}
      {...props}
    />
  );

  const renderNotice = notice => (
    <>
      <Title id="replace-card-title">{intl.messages['replace-card-heading']}</Title>
      <Banner $tone={notice.tone}>
        <div>
          <Message id={notice.id} />
        </div>
      </Banner>
      <Actions>{actionButton('close', onClose)}</Actions>
    </>
  );

  const renderSteps = () => (
    <Steps>
      {STEP_KEYS.map((key, i) => (
        <li
          key={key}
          data-done={i < step ? 'true' : 'false'}
          aria-current={i + 1 === step ? 'step' : undefined}
        >
          {intl.messages[`replace-card-step-${key}`]}
        </li>
      ))}
    </Steps>
  );

  const renderReason = () => (
    <>
      <Title id="replace-card-title">{intl.messages['replace-card-heading']}</Title>
      {renderSteps()}
      <FieldLabel id="replace-card-reason-label">
        {intl.messages['replace-card-reason-label']}
      </FieldLabel>
      <OptionList role="radiogroup" aria-labelledby="replace-card-reason-label">
        {REASONS.map(value => (
          <ChoiceRow
            key={value}
            type="radio"
            name="replace-card-reason"
            value={value}
            checked={reason === value}
            onChange={() => setReason(value)}
          >
            {intl.messages[`replace-card-reason-${value}`]}
          </ChoiceRow>
        ))}
      </OptionList>
      {DISPUTE_REASONS.includes(reason) && (
        <Banner $tone="warn">
          <div>
            <Message id="replace-card-dispute-alert" />
          </div>
        </Banner>
      )}
      <Actions>
        {actionButton('cancel', onClose)}
        {actionButton('continue', () => setStep(2), 'primary', { disabled: !reason })}
      </Actions>
    </>
  );

  const renderDelivery = () => {
    const fee = quote.fee_cents || 0;
    // Without a balance (it failed to load) only the fee is shown.
    const hasBalance = Number.isFinite(balanceCents);
    const balanceAfter = hasBalance ? balanceCents - fee : null;

    return (
      <>
        <Title id="replace-card-title">{intl.messages['replace-card-delivery-heading']}</Title>
        {renderSteps()}
        {mailingAddress && (
          <Address>
            <b>{intl.messages['replace-card-address-label']}</b>
            <br />
            {`${mailingAddress.first_name} ${mailingAddress.last_name}`}
            {formatAddress(mailingAddress).map(line => (
              <React.Fragment key={line}>
                <br />
                {line}
              </React.Fragment>
            ))}
          </Address>
        )}
        <FieldLabel>{intl.messages['replace-card-method-label']}</FieldLabel>
        {/* Standard mail is the only method; shown as the chosen option */}
        <ChoiceRow
          type="radio"
          name="replace-card-shipping"
          value="standard"
          checked
          onChange={() => {}}
        >
          {intl.messages['replace-card-method-standard']}
          <small>
            <Message
              id="replace-card-delivery-message"
              values={{
                from: DEFAULT_DELIVERY_WINDOW.min_business_days,
                to: DEFAULT_DELIVERY_WINDOW.max_business_days,
              }}
            />
          </small>
        </ChoiceRow>
        <Summary>
          {hasBalance && (
            <div>
              <dt>{intl.messages['replace-card-current-balance']}</dt>
              <dd>{money(balanceCents)}</dd>
            </div>
          )}
          <div>
            <dt>{intl.messages['replace-card-fee']}</dt>
            <dd>{feeDeduction(fee)}</dd>
          </div>
          {hasBalance && (
            <div>
              <dt>{intl.messages['replace-card-balance-after']}</dt>
              <dd data-negative={balanceAfter < 0 ? 'true' : 'false'}>
                {money(balanceAfter)}
              </dd>
            </div>
          )}
        </Summary>
        <DisclosureAlert type="warn">
          <Disclosures>
            <b>{intl.messages['replace-card-disclosures-heading']}</b>
            <ol>
              <li>
                <Message id="replace-card-disclosure-1" />
              </li>
              <li>
                <Message id="replace-card-disclosure-2" />
              </li>
              {/* With no fee there is nothing to disclose about one */}
              {quote.fee_cents > 0 && (
                <li>
                  <Message
                    id="replace-card-disclosure-3"
                    values={{ fee: money(quote.fee_cents) }}
                  />
                </li>
              )}
            </ol>
          </Disclosures>
        </DisclosureAlert>
        <ChoiceRow
          type="checkbox"
          checked={acknowledged}
          onChange={e => setAcknowledged(e.target.checked)}
          align="center"
        >
          {intl.messages['replace-card-acknowledge']}
        </ChoiceRow>
        <Actions>
          {actionButton('back', () => setStep(1))}
          {actionButton('continue', () => setStep(3), 'primary', { disabled: !acknowledged })}
        </Actions>
      </>
    );
  };

  const renderConfirm = () => {
    const submit = e => {
      e.preventDefault();
      if (!password) return;
      onSubmit({ reason, password });
    };

    return (
      <>
        <Title id="replace-card-title">{intl.messages['replace-card-confirm-heading']}</Title>
        {renderSteps()}
        <Summary>
          <div>
            <dt>{intl.messages['replace-card-summary-reason']}</dt>
            <dd>{intl.messages[`replace-card-summary-reason-${reason}`]}</dd>
          </div>
          {mailingAddress && (
            <div>
              <dt>{intl.messages['replace-card-summary-mail-to']}</dt>
              <dd>{formatAddress(mailingAddress).join(', ')}</dd>
            </div>
          )}
          <div>
            <dt>{intl.messages['replace-card-summary-fee']}</dt>
            <dd>{money(quote.fee_cents || 0)}</dd>
          </div>
        </Summary>
        {/* Enter in the password field submits */}
        <form onSubmit={submit}>
          <PasswordField>
            <span>{intl.messages['replace-card-password-label']}</span>
            <PasswordInput
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (passwordError) onPasswordChange();
              }}
              data-testid="replace-card-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder={intl.messages['replace-card-password-placeholder']}
              $invalid={passwordInvalid}
              aria-invalid={passwordInvalid}
              aria-describedby={passwordError ? 'replace-card-password-error' : undefined}
            />
          </PasswordField>
        </form>
        {passwordError && (
          <ErrorText id="replace-card-password-error" role="alert">
            {intl.messages[PASSWORD_ERROR_KEYS[passwordError]]}
          </ErrorText>
        )}
        <Banner $tone="bad">
          <div>
            <Message
              id="replace-card-cancel-warning"
              values={{ lastFour: quote.last_four_digits }}
            />
          </div>
        </Banner>
        {/* Outside the form: the shared Button's type prop is its colour, not a button type */}
        <Actions>
          {actionButton('back', () => setStep(2))}
          {actionButton('submit', submit, 'danger', { disabled: !password })}
        </Actions>
      </>
    );
  };

  // Replaces the confirm step the moment Replace card is pressed, so it never looks stuck.
  const renderSubmitting = () => (
    <Centered role="status">
      <Spinner />
      <b>{intl.messages['replace-card-submitting']}</b>
      <Hint as="p" style={{ textAlign: 'center' }}>
        {intl.messages['replace-card-submitting-hint']}
      </Hint>
    </Centered>
  );

  const renderSuccess = () => {
    const { from, to } = deliveryRange(new Date(), DEFAULT_DELIVERY_WINDOW);
    return (
      <>
        <Centered>
          <SuccessMark>
            <CheckMark size={28} />
          </SuccessMark>
          <Title id="replace-card-title" style={{ paddingRight: 0 }}>
            {intl.messages['replace-card-success-title']}
          </Title>
          <p>
            <FormattedMessage
              id="replace-card-success-expected"
              values={{
                from: <b>{intl.formatDate(from, dateFormat(intl.locale))}</b>,
                to: <b>{intl.formatDate(to, dateFormat(intl.locale))}</b>,
              }}
            />
          </p>
        </Centered>
        <Hint $size="13px" style={{ textAlign: 'center' }}>
          {intl.messages['replace-card-success-hint']}
        </Hint>
        <Actions>{actionButton('done', onClose, 'primary')}</Actions>
      </>
    );
  };

  const renderFailed = () => (
    <>
      <Title id="replace-card-title">{intl.messages['replace-card-heading']}</Title>
      <Banner $tone="bad">
        <div>
          <Message id="replace-card-failed" />
        </div>
      </Banner>
      <Actions>
        {actionButton('done', onClose)}
        {actionButton('try-again', onRetry, 'primary')}
      </Actions>
    </>
  );

  const renderBody = () => {
    if (NOTICES[phase]) return renderNotice(NOTICES[phase]);
    switch (phase) {
    case REPLACEMENT_PHASES.LOADING:
      return (
        <Centered role="status">
          <Spinner />
          <b>{intl.messages['replace-card-loading']}</b>
          <Hint as="p" style={{ textAlign: 'center' }}>
            {intl.messages['replace-card-loading-hint']}
          </Hint>
        </Centered>
      );
    case REPLACEMENT_PHASES.SUBMITTING:
      return renderSubmitting();
    case REPLACEMENT_PHASES.SUCCESS:
      return renderSuccess();
    case REPLACEMENT_PHASES.FAILED:
      return renderFailed();
    default:
      if (step === 1) return renderReason();
      if (step === 2) return renderDelivery();
      return renderConfirm();
    }
  };

  const submitting = phase === REPLACEMENT_PHASES.SUBMITTING;

  // No close control while the order is being placed: leaving cannot stop it.
  return createPortal(
    <Scrim>
      <Dialog
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="replace-card-title"
      >
        {!submitting && (
          <CloseButton
            type="button"
            aria-label={intl.messages['replace-card-close']}
            onClick={onClose}
          >
            &times;
          </CloseButton>
        )}
        <StepBody>
          {renderBody()}
        </StepBody>
      </Dialog>
    </Scrim>,
    document.getElementById('modal-root')
  );
};

ReplaceCardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  phase: PropTypes.string.isRequired,
  quote: PropTypes.shape({
    fee_cents: PropTypes.number,
    currency: PropTypes.string,
    last_four_digits: PropTypes.string,
  }),
  // The account's available balance in cents, from the dashboard; null when unknown.
  balanceCents: PropTypes.number,
  // The cardholder's address on file, from the dashboard's cardholder.
  mailingAddress: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postal_code: PropTypes.string,
  }),
  passwordError: PropTypes.string,
  // Clears passwordError once the cardholder types again.
  onPasswordChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ReplaceCardModal.defaultProps = {
  quote: null,
  balanceCents: null,
  mailingAddress: null,
  passwordError: null,
  onPasswordChange: () => {},
};

export default ReplaceCardModal;
